import { useField } from 'formik';
import { sortBy } from 'lodash-es';

import { DEMOGRAPHIC_QUOTA_TYPE } from 'util/demographics';
import { useHasRole } from 'hooks/users';
import { Question } from 'types/domainModels';
import {
  getQuestionOption,
  QUESTION_QUOTA_LOGICAL_MODIFIER_OPTIONS,
} from 'util/formOptions';

import FormCheckbox from 'components/common/forms/FormCheckbox';
import FormInput from '../../../common/forms/FormInput';
import FormSearchSelectInput from '../../../common/forms/FormSearchSelectInput';
import XButton from '../../../common/forms/XButton';

import { QuotaRowFormData } from './QuotasModal';

export const Quota = ({
  demographicQuestions,
  index,
  onClickRemove,
  usedOptions,
}: {
  demographicQuestions: Question[];
  index: number;
  onClickRemove?: () => void;
  usedOptions: { id: number | null; title: string }[];
}) => {
  const isAdmin = useHasRole('admin');

  const quotaNamePrefix = `modalQuotas.${index}`;
  const [{ value: logicalModifier }] = useField<
    QuotaRowFormData['logicalModifier']
  >(`${quotaNamePrefix}.logicalModifier`);
  const [{ value: selectedOption }, , optionHelpers] = useField<
    QuotaRowFormData['option']
  >(`${quotaNamePrefix}.option`);
  const [, , optionsHelpers] = useField(`${quotaNamePrefix}.options`);

  const availableOptions = selectedOption
    ? selectedOption.value.options.map((option) => ({
        id: option.id,
        title: option.title,
      }))
    : [];

  return (
    <div className="space-y-2">
      <div className="flex p-2 space-x-4">
        <FormSearchSelectInput
          borderColor="#D9D9D9"
          name={`${quotaNamePrefix}.option`}
          onChange={(newDemographic) => {
            optionHelpers.setValue(newDemographic ?? null);
            optionsHelpers.setValue([]);
          }}
          options={sortBy(demographicQuestions, (q) => q.sort)
            .filter(
              (question) =>
                question.questionTypeId === DEMOGRAPHIC_QUOTA_TYPE.ENUM,
            )
            .map((question) => {
              return getQuestionOption({ question });
            })}
          placeholder="Search for a demographic..."
        />
      </div>
      <div className="flex p-2 space-x-4">
        <div className="w-28">
          <FormSearchSelectInput
            name={`${quotaNamePrefix}.logicalModifier`}
            options={QUESTION_QUOTA_LOGICAL_MODIFIER_OPTIONS}
          />
        </div>
        {(logicalModifier?.value === 'at_most' ||
          logicalModifier?.value === 'at_least') && (
          <div className="w-24">
            <FormInput
              name={`${quotaNamePrefix}.numberNeeded`}
              size="md"
              type="number"
            />
          </div>
        )}
        <span className="w-22 text-sm">
          responses for
          <br />
          either
        </span>
        <div className="flex-grow w-2/6">
          <FormSearchSelectInput
            isMulti={true}
            name={`${quotaNamePrefix}.options`}
            options={availableOptions
              .filter(
                (option) =>
                  !usedOptions.some(
                    (usedOption) => usedOption.id === option.id,
                  ),
              )
              .map((option) => {
                return {
                  label: option.title,
                  value: { id: option.id, title: option.title },
                };
              })}
          />
        </div>
        <div className="mt-2">
          {onClickRemove && <XButton onClick={onClickRemove} title="Remove" />}
        </div>
      </div>
      {isAdmin &&
        (logicalModifier?.value === 'all' ||
          logicalModifier?.value === 'none') && (
          <FormCheckbox
            checkboxLabel="Quality Disqualification"
            name={`${quotaNamePrefix}.isQualityDisqualification`}
            tooltip="Classifies any respondent who does not meet the quota criteria as a Quality disqualification instead of a Question Quota disqualification. These respondents will not be factored into survey incidence."
          />
        )}
    </div>
  );
};
